import React, { useState, useRef } from 'react';
import '../Styles/newsletterbutton.css';
import rightArrowIcon from '../Assets/right-arrow.png';
import { trackEvent } from '../Utils/analytics';

/**
 * NewsletterForm component
 * Props:
 *   - onSubmit (optional): function(email) => void
 *   - style (optional): style object for the form container
 *   - inputProps (optional): props to spread onto the <input>
 *   - buttonProps (optional): props to spread onto the <button>
 *   - placeholder (optional): input placeholder
 *   - successText (optional): text to show on success
 *   - height (optional): height of input/button (default 44 for mobile, 38 for desktop)
 *   - width (optional): width of button (default 56 for mobile, 50 for desktop)
 *   - fontSize (optional): font size for input (default 16 for mobile, 14 for desktop)
 *   - mobile (optional): if true, uses mobile defaults
 */

// Use local API endpoint to avoid CORS issues
const SUBSCRIBE_ENDPOINT = '/api/subscribe';


function NewsletterForm({
  onSubmit,
  style = {},
  inputProps = {},
  buttonProps = {},
  placeholder = 'Enter email',
  successText = 'Subscribed!',
  height,
  width,
  fontSize,
  mobile = false
}) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Defaults for mobile/desktop
  const isMobile = mobile || (typeof window !== 'undefined' && window.innerWidth < 768);
  const inputHeight = height || (isMobile ? 44 : 38);
  const buttonWidth = width || (isMobile ? 56 : 50);
  const inputFontSize = fontSize || (isMobile ? 16 : 14);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    setLoading(true);
    let trimmedEmail = email.trim();
    // Simple but effective email regex (not overly strict)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      trackEvent('newsletter_submit_invalid');
      setLoading(false);
      return;
    }
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }
    try {
      const res = await fetch(SUBSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });
      if (res.ok) {
        setSubmitted(true);
        trackEvent('newsletter_subscribed');
        if (onSubmit) onSubmit(trimmedEmail);
        setEmail("");
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Subscription failed. Please try again.');
        trackEvent('newsletter_submit_failed', { status: res.status });
      }
    } catch (err) {
      setError('Network error. Please try again.');
      trackEvent('newsletter_submit_error');
    } finally {
      setLoading(false);
    }
  };

  // Custom validity for invalid (different for mobile/desktop)
  const handleInvalid = (e) => {
    const msg = isMobile ? 'You got this!' : 'Subscribe to never miss a launch!';
    if (!e.target.value) {
      e.target.setCustomValidity(msg);
      if (isMobile && window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate([50, 50, 50]);
      }
    } else {
      e.target.setCustomValidity('');
    }
  };

  // Remove custom validity on input
  const handleInput = (e) => {
    e.target.setCustomValidity('');
  };

  // Desktop: show tooltip on hover if empty
  const handleMouseOver = (e) => {
    if (!isMobile && inputRef.current && !inputRef.current.value) {
      inputRef.current.setCustomValidity('Subscribe to never miss a launch');
    }
  };
  const handleMouseOut = (e) => {
    if (!isMobile && inputRef.current) {
      inputRef.current.setCustomValidity('');
    }
  };

  if (submitted) {
    return (
      <span style={{
        color: '#45EBA5',
        fontSize: inputFontSize * 1.2,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        display: 'block',
        ...style
      }}>{successText}</span>
    );
  }

  // Use style.width if provided, otherwise default widths
  const outerWidth = style && style.width ? style.width : (isMobile ? '120%' : '100%');
  return (
    <div
      style={{
        width: outerWidth,
        maxWidth: isMobile ? 280 : undefined,
        margin: isMobile ? undefined : 0,
        textAlign: isMobile ? 'center' : 'left',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: 0,
          width: '100%',
          height: inputHeight,
          position: isMobile ? 'relative' : undefined,
          zIndex: isMobile ? 10 : undefined,
          ...style
        }}
      >
        <input
          ref={inputRef}
          type="email"
          inputMode="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={loading ? 'Subscribing...' : placeholder}
          autoComplete="email"
          spellCheck="false"
          style={{
            flex: 1,
            height: inputHeight,
            border: 'none',
            borderRadius: '4px 0 0 4px',
            padding: '0 12px',
            fontSize: inputFontSize,
            backgroundColor: '#fff',
            color: '#1c1c1c',
            outline: 'none',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            minWidth: 0,
            WebkitAppearance: 'none',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            fontFamily: 'inherit',
            ...(isMobile ? { flexBasis: '80%', maxWidth: 220 } : {}),
          }}
          required
          onInvalid={handleInvalid}
          onInput={handleInput}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          {...inputProps}
        />
        <button
          type="submit"
          disabled={loading}
          className="newsletter-button"
          style={{
            height: inputHeight,
            width: buttonWidth,
            // Only layout-related styles here
          }}
          aria-label="Submit newsletter signup"
          {...buttonProps}
        >
          <img
            src={rightArrowIcon}
            alt=""
            role="presentation"
            style={{ 
              width: 18, 
              height: 18, 
              opacity: loading ? 0.5 : 1,
              cursor: 'pointer',
              pointerEvents: 'none' // Ensures clicks go through to the button
            }}
            loading="lazy"
          />
        </button>
      </form>
      <div style={{
        fontSize: 11,
        textAlign: 'left',
        marginTop: 4,
        minHeight: 12,
        fontWeight: 400,
        letterSpacing: 0.1,
        marginLeft: isMobile ? 0 : 2,
        color: error ? '#ff6347' : '#999',
      }}>
        {error || 'No spam. Unsubscribe anytime'}
      </div>
    </div>
  );
}

export default NewsletterForm;
